import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { AnimatedNumber } from '../components/ui/AnimatedNumber'
import type { AiRoadmapGrowth } from './reporting-api'

const HEIGHT = 300
const LEFT = 48
const RIGHT = 22
const TOP = 28
const BOTTOM = 58
const MIN_WIDTH = 720
const QUARTER_WIDTH = 76

function initiativeLabel(count: number) {
  return `${count} ${count === 1 ? 'initiative' : 'initiatives'}`
}

export function AiRoadmapGrowthChart({ growth, loading }: { growth?: AiRoadmapGrowth; loading: boolean }) {
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null)
  const points = growth?.points ?? []
  const width = Math.max(MIN_WIDTH, LEFT + RIGHT + points.length * QUARTER_WIDTH)
  const chartWidth = width - LEFT - RIGHT
  const chartHeight = HEIGHT - TOP - BOTTOM
  const maxValue = Math.max(1, ...points.map((point) => point.totalInitiatives))

  return (
    <section className="panel ai-roadmap-growth" aria-labelledby="ai-roadmap-growth-heading">
      <div className="ai-roadmap-growth-heading">
        <div>
          <p className="eyebrow">Portfolio growth</p>
          <h2 id="ai-roadmap-growth-heading">AI roadmap growth</h2>
          <p>Planned initiatives and initiatives with active AI uses across the roadmap.</p>
        </div>
      </div>

      <div className="ai-roadmap-growth-metrics" aria-label="Current portfolio context">
        <Link to="/strategy"><strong>{loading ? '—' : <AnimatedNumber value={growth?.totalInitiatives ?? 0} />}</strong><span>AI initiatives</span></Link>
        <Link to="/ai-register"><strong>{loading ? '—' : <AnimatedNumber value={growth?.totalAiUses ?? 0} />}</strong><span>AI uses</span></Link>
        <Link to="/ai-register?summary=live"><strong>{loading ? '—' : <AnimatedNumber value={growth?.currentActiveAiUses ?? 0} />}</strong><span>currently active AI {growth?.currentActiveAiUses === 1 ? 'use' : 'uses'}</span></Link>
      </div>

      {loading ? <div className="ai-roadmap-chart-loading">Loading roadmap growth…</div> : (growth?.timedInitiatives ?? 0) > 0 && points.length ? (
        <>
          <div className="ai-roadmap-legend" aria-label="Chart legend">
            <span><i className="planned" /> Planned</span>
            <span><i className="active" /> Active</span>
            <span><i className="unavailable" /> Active classification unavailable</span>
          </div>
          <div className="ai-roadmap-chart-scroll" tabIndex={0} aria-label="Scrollable AI roadmap growth chart">
            <svg className="ai-roadmap-chart" style={{ minWidth: `${width}px` }} viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-labelledby="ai-roadmap-chart-title ai-roadmap-chart-description">
              <title id="ai-roadmap-chart-title">AI roadmap initiatives by quarter</title>
              <desc id="ai-roadmap-chart-description">Stacked quarterly bars count AI Initiatives. Reliable quarters split Initiatives into Planned and Active. Hatched bars show roadmap totals where historic or future active classification is unavailable.</desc>
              <defs>
                <pattern id="roadmap-unavailable-pattern" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="8" height="8" className="ai-roadmap-unavailable-base" />
                  <line x1="0" y1="0" x2="0" y2="8" className="ai-roadmap-unavailable-line" />
                </pattern>
              </defs>
              <line className="ai-roadmap-axis" x1={LEFT} y1={TOP + chartHeight} x2={width - RIGHT} y2={TOP + chartHeight} />
              <line className="ai-roadmap-gridline" x1={LEFT} y1={TOP} x2={width - RIGHT} y2={TOP} />
              <line className="ai-roadmap-gridline" x1={LEFT} y1={TOP + chartHeight / 2} x2={width - RIGHT} y2={TOP + chartHeight / 2} />
              <text className="ai-roadmap-y-label" x={LEFT - 10} y={TOP + 4}>{maxValue}</text>
              <text className="ai-roadmap-y-label" x={LEFT - 10} y={TOP + chartHeight / 2 + 4}>{Math.round(maxValue / 2)}</text>
              <text className="ai-roadmap-y-label" x={LEFT - 10} y={TOP + chartHeight + 4}>0</text>
              {points.map((point, index) => {
                const slot = chartWidth / points.length
                const barWidth = Math.min(44, slot * 0.58)
                const x = LEFT + index * slot + (slot - barWidth) / 2
                const totalHeight = point.totalInitiatives / maxValue * chartHeight
                const activeCount = point.activeInitiatives
                const plannedHeight = activeCount === null ? totalHeight : point.plannedInitiatives / maxValue * chartHeight
                const activeHeight = activeCount === null ? 0 : activeCount / maxValue * chartHeight
                const baseY = TOP + chartHeight
                const accessibleLabel = activeCount === null
                  ? `${point.label} — ${initiativeLabel(point.totalInitiatives)} on the roadmap; active classification unavailable`
                  : `${point.label} — ${initiativeLabel(point.plannedInitiatives)} planned and ${initiativeLabel(activeCount)} active`
                return <g
                  key={point.quarterStart}
                  className={`ai-roadmap-quarter-group ${point.quality}`}
                  style={{ '--swell-bar-delay': `${90 + index * 55}ms` } as CSSProperties}
                  tabIndex={0}
                  role="img"
                  aria-label={accessibleLabel}
                  onMouseEnter={() => setActiveTooltip(index)}
                  onMouseLeave={() => setActiveTooltip((current) => current === index ? null : current)}
                  onFocus={() => setActiveTooltip(index)}
                  onBlur={() => setActiveTooltip((current) => current === index ? null : current)}
                >
                  <title>{accessibleLabel}</title>
                  {activeCount === null ? (
                    <rect className="ai-roadmap-bar unavailable" x={x} y={baseY - totalHeight} width={barWidth} height={totalHeight} rx="4" fill="url(#roadmap-unavailable-pattern)" />
                  ) : (
                    <>
                      {point.plannedInitiatives > 0 ? <rect className="ai-roadmap-bar planned" x={x} y={baseY - plannedHeight} width={barWidth} height={plannedHeight} rx={activeHeight ? 0 : 4} /> : null}
                      {activeCount > 0 ? <rect className="ai-roadmap-bar active" x={x} y={baseY - plannedHeight - activeHeight} width={barWidth} height={activeHeight} rx="4" /> : null}
                    </>
                  )}
                  <text className="ai-roadmap-total-label" x={x + barWidth / 2} y={Math.max(TOP + 12, baseY - totalHeight - 7)}>{point.totalInitiatives}</text>
                  <text className="ai-roadmap-x-label" x={x + barWidth / 2} y={HEIGHT - 18}>{point.label}</text>
                </g>
              })}
              {points.map((point, index) => {
                const slot = chartWidth / points.length
                const barWidth = Math.min(44, slot * 0.58)
                const x = LEFT + index * slot + (slot - barWidth) / 2
                const totalHeight = point.totalInitiatives / maxValue * chartHeight
                const activeCount = point.activeInitiatives
                const baseY = TOP + chartHeight
                const tooltipWidth = 238
                const tooltipX = Math.min(width - tooltipWidth - 5, Math.max(5, x + barWidth / 2 - tooltipWidth / 2))
                const tooltipY = Math.max(5, baseY - totalHeight - 54)
                return <g key={`tooltip-${point.quarterStart}`} className={`ai-roadmap-tooltip${activeTooltip === index ? ' visible' : ''}`} aria-hidden="true">
                  <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height="42" rx="7" />
                  <text x={tooltipX + tooltipWidth / 2} y={tooltipY + 17}>{point.label}</text>
                  <text className="detail" x={tooltipX + tooltipWidth / 2} y={tooltipY + 32}>{activeCount === null ? `${initiativeLabel(point.totalInitiatives)} · Active unavailable` : `${point.plannedInitiatives} planned · ${activeCount} active`}</text>
                </g>
              })}
            </svg>
          </div>
        </>
      ) : <p className="ai-roadmap-empty">Add Initiative roadmap timing to show quarterly portfolio growth.</p>}
    </section>
  )
}
